import React, { useCallback, useState, useEffect, useRef } from "react";
import { hooks } from "botframework-webchat-component";
import { FaPaperPlane } from "react-icons/fa";
import MessageItem from "./MessageItem";
import WelcomeTiles from "./WelcomeTiles";
import SuggestionList from "./SuggestionList";
import { RiUserSmileLine } from "react-icons/ri";
import { BiHeadphone } from "react-icons/bi";
import { PiWaveform } from "react-icons/pi";
import SpeechToText from "./SpeechToText";
import HandsFreeMode from "./HandsFreeMode";
import { sanitizeString } from "../../utils/sanitize";
import Offers from "./Offers";
import OfferDetail from './OfferDetail';
import {
  buildApiHeaders,
  buildApiUrl,
  getQueryBootstrap,
} from "../../utils/apiBootstrap";
const { useActivities, useSendMessage, useSendEvent } = hooks;

// INTERNALLY USED COMPONENT(S)

const convertArabicToEnglishDigits = (str) => {
  const arabicDigits = "٠١٢٣٤٥٦٧٨٩"; // Arabic digits
  const englishDigits = "0123456789"; // English digits
  return str
    .split("")
    .map((char) => {
      const index = arabicDigits.indexOf(char);
      return index !== -1 ? englishDigits[index] : char;
    })
    .join("");
};



const formatDateLabel = (date,isArabic) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const activityDate = new Date(date);

  let timeString = isArabic
    ? activityDate.toLocaleTimeString("ar-EG", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // Ensures AM/PM is used in Arabic
      })
    : activityDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // Ensures AM/PM is used in English
      });

  timeString = convertArabicToEnglishDigits(timeString);

  if (isArabic) {
    timeString = timeString.replace("ص", "صباحاً").replace("م", "مساءً");
  }

  if (activityDate.toDateString() === today.toDateString()) {
    return isArabic ? `اليوم  ${timeString}` : `Today at ${timeString}`;
  }

  if (activityDate.toDateString() === yesterday.toDateString()) {
    return isArabic ? `أمس في ${timeString}` : `Yesterday at ${timeString}`;
  }

  return isArabic
    ? activityDate.toLocaleString("ar-EG", { hour12: true }) // Full Arabic date
    : activityDate.toLocaleString(); // Default locale
};

const DateDivider = ({ date,isArabic }) => (
  <div className="flex items-center my-4">
    <hr className="flex-grow border-gray-300" />
    <span className="mx-2 text-gray-500 text-sm">
      {formatDateLabel(date,isArabic)}
    </span>{" "}
    <hr className="flex-grow border-gray-300" />
  </div>
);


const ChatContent = ({ isArabic }) => {
console.log("TESTING ENV VAR", process.env.APP_HOST);
  //HOOKS INTIALIZATION
  const [apiBootstrap, setApiBootstrap] = useState(() => getQueryBootstrap());
  const [activities] = useActivities();
  const sendMessage = useSendMessage();
  const sendEvent = useSendEvent();
  const [sendBoxValue, setSendBoxValue] = useState("");
  const messagesEndRef = useRef(null);
  const [streamingText, setStreamingText] = useState("");
  const processedActivityIds = useRef(new Set()); // To track processed activities
  const prevActivitiesRef = useRef([]);
  const [suggestions, setSuggestions] = useState([]); // State for suggestions
  const [isThinking, setIsThinking] = useState(false);
  const [isSurveyButtonDisabled, setSurveyButtonDisabled] = useState(false); // State for button disable
  const [isRecording, setIsRecording] = useState(false);
  const [isHandsFreeActive, setIsHandsFreeActive] = useState(false);
  const [surveyMapping, setSurveyMapping]= useState({})
  const showHandsFreeButton = false;

  const [responseFeedbackMapping, setResponseFeedbackMapping] = useState({}); // State for suggestions
  const [arabicMessages, setArabicMessages] = useState({}); // State for suggestions
  const [userLastMessageArabic, setUserLastMessageArabic] = useState(isArabic);
  const processedSuggestionIdsRef = useRef(new Set());
  const [autoScrollDisabled, setAutoScrollDisabled] = useState(false);
  const disableTimeoutRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef(null);
  const [offerDataMapping, setOfferDataMapping] = useState({}); // New state for tracking offer data
  const [pendingActionCardsMapping, setPendingActionCardsMapping] = useState({}); // Move-in pending-action cards
  const [paymentReceiptMapping, setPaymentReceiptMapping] = useState({}); // Payment receipt cards
  const pendingOfferRef = useRef(null);
  const [offerDetailMapping, setOfferDetailMapping] = useState({});
  const [redeemDataMapping, setRedeemDataMapping] = useState({}); // Add state for redeem data
  const [viewDetailsMapping, setViewDetailsMapping] = useState({}); // Add state for view details
  const shouldScrollForOfferDetail = useRef(false);
  const shouldScrollForRedeem = useRef(false);
  const shouldScrollForViewDetails = useRef(false); // Add ref for view details
  const offerDetailScrollTimeout = useRef(null);
  const redeemScrollTimeout = useRef(null);
  const viewDetailsScrollTimeout = useRef(null); // Add timeout ref for view details

  // Near the top of your file, add this constant
  // const VAULT_URL = process.env.REACT_APP_VAULT_SECRET_URL || '';

  //ESSENTIAL CALLBACK FUNCTIONS
  const [basicConfig, setBasicConfig] = useState(null);

  useEffect(() => {
    setApiBootstrap(() => getQueryBootstrap());
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    if (!apiBootstrap.apiBaseUrl) {
      return () => controller.abort();
    }

    const fetchBasicConfig = async () => {
      try {
        const res = await fetch(
          buildApiUrl(
            apiBootstrap.apiBaseUrl,
            `/basic_config?language=${isArabic ? 'ar' : 'en'}`
          ),
          {
            method: "GET",
            headers: buildApiHeaders({
              Accept: "application/json",
            }),
            signal: controller.signal,
          }
        );

        if (!res.ok) throw new Error(`Failed to fetch basic_config: ${res.status}`);

        const json = await res.json();
        console.log("Fetched basic_config:", json);
        setBasicConfig(json);
        // use the config as needed, e.g. setSurveyMapping or other state based on json
        // example: if (json.surveyEnabled) setSurveyButtonDisabled(false);
      } catch (err) {
        if (err.name === "AbortError") return; // request was cancelled on unmount
        console.error("Error fetching basic_config:", err);
      }
    };

    fetchBasicConfig();

    return () => controller.abort();
  }, [apiBootstrap.apiBaseUrl, isArabic]);

  const updateSuggestions = useCallback((parsedValue) => {
    setSuggestions(parsedValue.queries);
    setSurveyButtonDisabled(false);
  }, []);

  const handleChange = useCallback(
    ({ target: { value } }) => setSendBoxValue(value),
    []
  );

  const createUserActivity = (text) => ({
    type: "message",
    channelData: { role: "user" },
    text: sanitizeString(text),
  });

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const sanitizedValue = sanitizeString(sendBoxValue.trim());
      if (sanitizedValue) {
        try {
          const userActivity = createUserActivity(sanitizedValue);
          await sendMessage(sanitizedValue, undefined, {
            channelData: userActivity.channelData,
          });
          setIsThinking(true);
          setSendBoxValue("");
          setSuggestions([]);
          setSurveyButtonDisabled(true);
        } catch (error) {
          console.error("Error sending message:", error);
        }
      }
    },
    [sendBoxValue, sendMessage]
  );

  const onDemandSurvey = useCallback(() => {
    if (isSurveyButtonDisabled) return;
    setSurveyButtonDisabled(true);
    setSuggestions([]);
    sendEvent("survey");
  }, [isSurveyButtonDisabled, sendEvent]);

  const handleArabicSurveyKeyDown = useCallback((event) => {
    if (!isArabic || event.key !== "Tab" || event.shiftKey) {
      return;
    }

    const composerInput = document.getElementById("chat-composer-input");
    if (composerInput) {
      event.preventDefault();
      composerInput.focus();
    }
  }, [isArabic]);

  const onHandsFreeToggle = useCallback((isActive) => {
    setIsHandsFreeActive(isActive);
    console.log('Hands-free mode:', isActive ? 'activated' : 'deactivated');
  }, []);

  const clearState = () => {
    setSendBoxValue("");
    setSuggestions([]);
  };

  const onSuggestionClick = useCallback(
    (suggestion) => {
      setIsThinking(true);
      setSurveyButtonDisabled(true);
      sendMessage(sanitizeString(suggestion));
      clearState();
    },
    [sendMessage]
  );

  //SCROLLING FUNCTION
  const handleUserScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    // Set scrolling state to true and show scrollbar
    setIsScrolling(true);
    
    // Clear any existing timer
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }

    // Set new timer to hide scrollbar after 3 seconds
    scrollTimerRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 3000);

    if (!isAtBottom && scrollTop < lastScrollTopRef.current) {
      setAutoScrollDisabled(true);

      if (disableTimeoutRef.current) {
        clearTimeout(disableTimeoutRef.current);
      }

      disableTimeoutRef.current = setTimeout(() => {
        setAutoScrollDisabled(false);
      }, 3000);
    }

    lastScrollTopRef.current = scrollTop;
  };


// USE EFFECT HOOKS USED FOR LOGIC
  useEffect(() => {
    const cleanedActivities = activities.filter(
      (activity) => activity.type !== "typing"
    );
    console.log("cleaned", cleanedActivities);

    if (activities.length >= 2) {
      const lastActivity = activities[activities.length - 1];
      const penultimateActivity = activities[activities.length - 2];
      
      // Add direct scroll trigger for specific events
      if (lastActivity.type === "event" && 
          (lastActivity.name === "OfferDetails" || lastActivity.name === "redeemOffer")) {
        console.log("Direct scroll trigger for:", lastActivity.name);
        
        // Wait for DOM to update before scrolling - single scroll only
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 800); // Single scroll at 2ms
      }
      
      if (lastActivity?.from?.name?.toLowerCase() === 'bot' && lastActivity?.channelData?.streamType?.toLowerCase() === 'final') {
        setIsThinking(false);
      }

      // Handle survey button state based on activities
      if (lastActivity.type === "message" && penultimateActivity.type === "typing") {
        setSurveyButtonDisabled(false);
      }

      if (lastActivity.type === "event" && lastActivity.name === "submitFeedback") {
        setSurveyButtonDisabled(false);
      }

      if (lastActivity.type === "event" && lastActivity.name === "surveyData") {
        setSurveyButtonDisabled(true);
      }

      // Handle disabling interactions for offer-related events
      if (lastActivity.type === "event" && 
          (lastActivity.name === "requestOffer" || 
           lastActivity.name === "viewMoreOffers" ||
           lastActivity.name === "disableInteractions" ||
           lastActivity.name === "addToFavorite" ||
           lastActivity.name === "removeFromFavourite" ||
           lastActivity.name === "viewDetails")) { // Added viewDetails
        setSurveyButtonDisabled(true);
        setIsThinking(true);
      }
      
      // Handle favoritesUpdated event from backend - IMPROVED VERSION
      if (lastActivity.type === "event" && lastActivity.name === "favoritesUpdated") {
        console.log('=== ChatContent received favoritesUpdated event ===');
        console.log('Event value:', lastActivity.value);
        
        const { offerUno, action, contractAccNumber } = lastActivity.value;
        
        if (offerUno && contractAccNumber) {
          const storageKey = `dewaChatbotFavorites_${contractAccNumber}`;
          console.log('Using storage key:', storageKey);
          
          // Get current favorites from localStorage
          let currentFavorites = {};
          try {
            const savedFavorites = localStorage.getItem(storageKey);
            currentFavorites = savedFavorites ? JSON.parse(savedFavorites) : {};
            console.log('Current favorites from localStorage:', currentFavorites);
          } catch (error) {
            console.error('Error reading favorites from localStorage:', error);
            currentFavorites = {};
          }
          
          // Update the specific offer based on the backend confirmation
          const newFavoriteValue = action === 'add';
          currentFavorites[offerUno] = newFavoriteValue;
          
          console.log('Updated favorites object:', currentFavorites);
          console.log(`Setting ${offerUno} to ${newFavoriteValue} based on action: ${action}`);
          
          // Save back to localStorage
          try {
            localStorage.setItem(storageKey, JSON.stringify(currentFavorites));
            console.log('Saved to localStorage successfully');
          } catch (error) {
            console.error('Error saving favorites to localStorage:', error);
          }
          
          // Dispatch the custom event - use setTimeout to ensure it's processed
          setTimeout(() => {
            console.log('=== Dispatching custom favoritesUpdated event ===');
            console.log('Event detail:', { storageKey, favorites: currentFavorites });
            
            const customEvent = new CustomEvent('favoritesUpdated', {
              detail: {
                storageKey,
                favorites: currentFavorites
              }
            });
            
            window.dispatchEvent(customEvent);
            console.log('Custom event dispatched');
          }, 100);
        }
        
        // Enable interactions after processing
        setIsThinking(false);
        setSurveyButtonDisabled(false);
      }

      // Any bot message should reset the thinking/disabled state for offer-related operations
      if (lastActivity.type === "message" && lastActivity.from?.name?.toLowerCase() === 'bot') {
        // Check if the previous activity was related to offers or favorites
        let shouldReset = false;
        
        // Look back through recent activities to find the last event
        for (let i = activities.length - 2; i >= Math.max(0, activities.length - 5); i--) {
          const prevActivity = activities[i];
          if (prevActivity.type === "event" && 
              (prevActivity.name === "addToFavorite" || 
               prevActivity.name === "removeFromFavourite" ||
               prevActivity.name === "requestOffer" ||
               prevActivity.name === "viewMoreOffers" ||
               prevActivity.name === "disableInteractions" ||
               prevActivity.name === "viewDetails")) { // Added viewDetails
            shouldReset = true;
            break;
          }
        }
        
        if (shouldReset) {
          console.log("Resetting thinking state after bot response");
          setIsThinking(false);
          setSurveyButtonDisabled(false);
        }
      }

      // Handle favorite operation errors
      if (lastActivity?.type === "message" && 
          lastActivity?.from?.name?.toLowerCase() === 'bot' && 
          penultimateActivity?.type === "event" && 
          (penultimateActivity?.name === "addToFavorite" || 
           penultimateActivity?.name === "removeFromFavourite")) {
        
        const responseText = lastActivity.text.toLowerCase();
        const isError = responseText.includes('log in') || 
                       responseText.includes('error') || 
                       responseText.includes('failed') ||
                       responseText.includes('لم يتم') || // Arabic: "was not"
                       responseText.includes('يرجى تسجيل الدخول'); // Arabic: "please log in"
        
        if (isError) {
          console.log('Favorite operation failed - no localStorage update needed');
        }
        
        // Enable interactions regardless of success/failure
        setIsThinking(false);
        setSurveyButtonDisabled(false);
      }
    }

    activities.forEach((activity) => {
      // Translate message text
      if (activity.type === "message" && activity.text) {
        const translate = activity.text.split("|||");
        activity.text = translate.length > 1 && userLastMessageArabic
          ? translate[1]
          : translate[0];
      }

      // Handle streaming text
      if (
        activity.type === "typing" &&
        activity.channelData?.streamType === "streaming" &&
        !processedActivityIds.current.has(activity.id)
      ) {
        setStreamingText((prev) =>
          prev ? `${prev}${activity.text}` : activity.text
        );
        if (streamingText !== "") setIsThinking(false);
        processedActivityIds.current.add(activity.id);
      } else if (activity.type === "message") {
        setStreamingText("");
        processedActivityIds.current.clear();
      }

      // Handle suggestions
      if (
        activity.type === "event" &&
        activity.name === "suggestions" &&
        !processedSuggestionIdsRef.current.has(activity.id)
      ) {
        try {
          let parsedValue = activity.value;
          if (typeof parsedValue === "string") {
            parsedValue = JSON.parse(parsedValue);
          }

          if (
            typeof parsedValue === "object" &&
            parsedValue !== null &&
            Array.isArray(parsedValue.queries)
          ) {
            updateSuggestions(parsedValue, activity.id);
            processedSuggestionIdsRef.current.add(activity.id);
          } else {
            console.error("Invalid suggestions format:", parsedValue);
          }
        } catch (error) {
          console.error("Failed to parse suggestions:", error);
        }
      }

      // Handle Arabic messages mapping
      if (
        activities.length &&
        activities[activities.length - 1].type === "message"
      ) {
        const lastActivity = activities[activities.length - 1];
        setArabicMessages((prev) => ({
          ...prev,
          [lastActivity.id]: userLastMessageArabic,
        }));
      }
    });
  }, [activities, updateSuggestions, userLastMessageArabic, streamingText]);


  useEffect(() => {
    const prevActivities = prevActivitiesRef.current;
    const newActivities = activities.filter(
      (activity) => !prevActivities.find((a) => a.id === activity.id)
    );

    const hasNewMessage = newActivities.some(
      (activity) => activity.type === "message" || activity.type === "typing"
    );

    // Check if any new bot message has offer details or redeem data
    const hasOfferDetailMessage = newActivities.some(
      (activity) => 
        activity.type === "message" && 
        activity.from?.name?.toLowerCase() === 'bot' &&
        (offerDetailMapping[activity.id] || redeemDataMapping[activity.id] || viewDetailsMapping[activity.id])
    );

    if ((hasNewMessage || hasOfferDetailMessage) && !autoScrollDisabled) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 10);
      });
    }

    prevActivitiesRef.current = activities;

    const lastMessage = activities
      .filter((activity) => activity.type === "message")
      .pop();

    if (lastMessage) {
      const messageText = lastMessage.text;
      const arabicRegex = /[\u0600-\u06FF]/;
      const isArabicMessage = arabicRegex.test(messageText);
      const isNumeric = /^-?\d+(\.\d+)?$/.test(messageText);

      if (!messageText || messageText === "") {
        return;
      } else if (isNumeric) {
        return;
      } else if (isArabicMessage && userLastMessageArabic !== true) {
        setUserLastMessageArabic(true);
      } else if (!isArabicMessage && userLastMessageArabic !== false) {
        setUserLastMessageArabic(false);
      }
    }
  }, [activities, autoScrollDisabled, userLastMessageArabic, offerDetailMapping, redeemDataMapping, viewDetailsMapping]);
  useEffect(() => {
    setSurveyMapping((prevSurveyMapping) => {
      const newSurveyMapping = { ...prevSurveyMapping };

      activities.forEach((activity) => {
        if (
          activity.type === "event" &&
          activity.name === "submitFeedback"
        ) {
          newSurveyMapping[activity.value.id] = activity.value.data;
        }
      });

      return newSurveyMapping;
    });

    setResponseFeedbackMapping(() => {
      const newFeedbackMapping = {};

      activities.forEach((activity) => {
        if (
          activity.type === "event" &&
          (activity.name === "ThumbsUp" || activity.name === "ThumbsDown")
        ) {
          const feedbackValue = activity.name === "ThumbsUp" ? 1 : -1;
          const messageId = activity.value;

          newFeedbackMapping[messageId] =
            newFeedbackMapping[messageId] === feedbackValue
              ? 0
              : feedbackValue;
        }
      });

      return newFeedbackMapping;
    });
  }, [activities]);

  useEffect(() => {
    // Process offer events and map them to the next bot message
    const processOfferEvents = () => {
      // Use a temporary mapping to track which offers have been processed
      const processedOfferIds = new Set();
      const newOfferDataMapping = {...offerDataMapping};
      
      // Process activities in order
      for (let i = 0; i < activities.length - 1; i++) {
        const currentActivity = activities[i];
        
        // Check if this is an unprocessed offer event
        if (currentActivity.type === "event" && 
            currentActivity.name === "offerData" && 
            !processedOfferIds.has(currentActivity.id)) {
          
          // Mark this offer as processed
          processedOfferIds.add(currentActivity.id);
          
          // Look for the next bot message after this offer
          for (let j = i + 1; j < activities.length; j++) {
            const nextActivity = activities[j];
            if (nextActivity.type === "message" && 
                nextActivity.from?.name?.toLowerCase() === 'bot') {
              
              // Associate this offer with the next bot message
              newOfferDataMapping[nextActivity.id] = currentActivity.value;
              console.log("Mapped offer to message:", nextActivity.id);
              break;
            }
          }
        }
      }
      
      // Only update if the mapping has actually changed
      if (JSON.stringify(newOfferDataMapping) !== JSON.stringify(offerDataMapping)) {
        setOfferDataMapping(newOfferDataMapping);
      }
    };

    // Run the processing function whenever activities change
    if (activities.length > 0) {
      processOfferEvents();
    }
  }, [activities, offerDataMapping]);

  useEffect(() => {
    // Process pending-action-card events (move-in) and map each to the next
    // bot message, mirroring the offerData handling above.
    const processPendingActionCardEvents = () => {
      const processedIds = new Set();
      const newMapping = { ...pendingActionCardsMapping };

      for (let i = 0; i < activities.length - 1; i++) {
        const currentActivity = activities[i];

        if (
          currentActivity.type === "event" &&
          currentActivity.name === "pendingActionCards" &&
          !processedIds.has(currentActivity.id)
        ) {
          processedIds.add(currentActivity.id);

          for (let j = i + 1; j < activities.length; j++) {
            const nextActivity = activities[j];
            if (
              nextActivity.type === "message" &&
              nextActivity.from?.name?.toLowerCase() === "bot"
            ) {
              newMapping[nextActivity.id] = currentActivity.value;
              break;
            }
          }
        }
      }

      if (JSON.stringify(newMapping) !== JSON.stringify(pendingActionCardsMapping)) {
        setPendingActionCardsMapping(newMapping);
      }
    };

    if (activities.length > 0) {
      processPendingActionCardEvents();
    }
  }, [activities, pendingActionCardsMapping]);

  useEffect(() => {
    // Process paymentReceipt events and map each to the next bot message so the
    // React PaymentReceiptCard renders it (mirrors the pendingActionCards flow).
    const processPaymentReceiptEvents = () => {
      const processedIds = new Set();
      const newMapping = { ...paymentReceiptMapping };

      for (let i = 0; i < activities.length - 1; i++) {
        const currentActivity = activities[i];

        if (
          currentActivity.type === "event" &&
          currentActivity.name === "paymentReceipt" &&
          !processedIds.has(currentActivity.id)
        ) {
          processedIds.add(currentActivity.id);

          for (let j = i + 1; j < activities.length; j++) {
            const nextActivity = activities[j];
            if (
              nextActivity.type === "message" &&
              nextActivity.from?.name?.toLowerCase() === "bot"
            ) {
              newMapping[nextActivity.id] = currentActivity.value;
              break;
            }
          }
        }
      }

      if (JSON.stringify(newMapping) !== JSON.stringify(paymentReceiptMapping)) {
        setPaymentReceiptMapping(newMapping);
      }
    };

    if (activities.length > 0) {
      processPaymentReceiptEvents();
    }
  }, [activities, paymentReceiptMapping]);

  useEffect(() => {
    // Process offer detail events and map them to the next bot message
    const processOfferDetailEvents = () => {
      // Use a temporary mapping to track which offer details have been processed
      const processedOfferDetailIds = new Set();
      const newOfferDetailMapping = {...offerDetailMapping};
      let hasNewOfferDetail = false;
      
      // Process activities in order
      for (let i = 0; i < activities.length - 1; i++) {
        const currentActivity = activities[i];
        
        // Check if this is an unprocessed offer detail event
        if (currentActivity.type === "event" && 
            currentActivity.name === "OfferDetails" && 
            !processedOfferDetailIds.has(currentActivity.id)) {
          
          // Mark this offer detail as processed
          processedOfferDetailIds.add(currentActivity.id);
          hasNewOfferDetail = true;
          
          // Look for the next bot message after this offer detail
          for (let j = i + 1; j < activities.length; j++) {
            const nextActivity = activities[j];
            if (nextActivity.type === "message" && 
                nextActivity.from?.name?.toLowerCase() === 'bot') {
              
              // Associate this offer detail with the next bot message
              newOfferDetailMapping[nextActivity.id] = currentActivity.value;
              console.log("Mapped offer detail to message:", nextActivity.id);
              break;
            }
          }
        }
      }
      
      // Only update if the mapping has actually changed
      if (JSON.stringify(newOfferDetailMapping) !== JSON.stringify(offerDetailMapping)) {
        setOfferDetailMapping(newOfferDetailMapping);
        
        // Set flag to trigger scrolling after DOM updates
        if (hasNewOfferDetail && !autoScrollDisabled) {
          shouldScrollForOfferDetail.current = true;
        }
      }
    };

    // Run the processing function whenever activities change
    if (activities.length > 0) {
      processOfferDetailEvents();
    }
  }, [activities, offerDetailMapping, autoScrollDisabled]);

  // Add new useEffect for redeem events
  useEffect(() => {
    // Process redeem events and map them to the next bot message
    const processRedeemEvents = () => {
      // Use a temporary mapping to track which redeem events have been processed
      const processedRedeemIds = new Set();
      const newRedeemDataMapping = {...redeemDataMapping};
      let hasNewRedeem = false;
      
      // Process activities in order
      for (let i = 0; i < activities.length - 1; i++) {
        const currentActivity = activities[i];
        
        // Check if this is an unprocessed redeem event
        if (currentActivity.type === "event" && 
            (currentActivity.name === "redeemOffer" || 
             currentActivity.name === "redeemData" ||
             currentActivity.name === "offerRedeem") && 
            !processedRedeemIds.has(currentActivity.id)) {
          
          // Mark this redeem event as processed
          processedRedeemIds.add(currentActivity.id);
          hasNewRedeem = true;
          
          // Look for the next bot message after this redeem event
          for (let j = i + 1; j < activities.length; j++) {
            const nextActivity = activities[j];
            if (nextActivity.type === "message" && 
                nextActivity.from?.name?.toLowerCase() === 'bot') {
              
              // Associate this redeem event with the next bot message
              newRedeemDataMapping[nextActivity.id] = currentActivity.value;
              console.log("Mapped redeem data to message:", nextActivity.id);
              break;
            }
          }
        }
      }
      
      // Only update if the mapping has actually changed
      if (JSON.stringify(newRedeemDataMapping) !== JSON.stringify(redeemDataMapping)) {
        setRedeemDataMapping(newRedeemDataMapping);
        
        // Set flag to trigger scrolling after DOM updates
        if (hasNewRedeem && !autoScrollDisabled) {
          shouldScrollForRedeem.current = true;
        }
      }
    };

    // Run the processing function whenever activities change
    if (activities.length > 0) {
      processRedeemEvents();
    }
  }, [activities, redeemDataMapping, autoScrollDisabled]);

  // Add new useEffect for view details events
  useEffect(() => {
    // Process view details events and map them to the next bot message
    const processViewDetailsEvents = () => {
      // Use a temporary mapping to track which view details events have been processed
      const processedViewDetailsIds = new Set();
      const newViewDetailsMapping = {...viewDetailsMapping};
      let hasNewViewDetails = false;
      
      // Process activities in order
      for (let i = 0; i < activities.length - 1; i++) {
        const currentActivity = activities[i];
        
        // Check if this is an unprocessed view details event
        if (currentActivity.type === "event" && 
            (currentActivity.name === "viewDetails" ||
             currentActivity.name === "viewDetailsData") && 
            !processedViewDetailsIds.has(currentActivity.id)) {
          
          // Mark this view details event as processed
          processedViewDetailsIds.add(currentActivity.id);
          hasNewViewDetails = true;
          
          // Look for the next bot message after this view details event
          for (let j = i + 1; j < activities.length; j++) {
            const nextActivity = activities[j];
            if (nextActivity.type === "message" && 
                nextActivity.from?.name?.toLowerCase() === 'bot') {
              
              // Associate this view details event with the next bot message
              newViewDetailsMapping[nextActivity.id] = currentActivity.value;
              console.log("Mapped view details to message:", nextActivity.id);
              break;
            }
          }
        }
      }
      
      // Only update if the mapping has actually changed
      if (JSON.stringify(newViewDetailsMapping) !== JSON.stringify(viewDetailsMapping)) {
        setViewDetailsMapping(newViewDetailsMapping);
        
        // Set flag to trigger scrolling after DOM updates
        if (hasNewViewDetails && !autoScrollDisabled) {
          shouldScrollForViewDetails.current = true;
        }
      }
    };

    // Run the processing function whenever activities change
    if (activities.length > 0) {
      processViewDetailsEvents();
    }
  }, [activities, viewDetailsMapping, autoScrollDisabled]);

  // Remove or comment out the separate useEffect for redeem scrolling to avoid double scrolling
  /*
  useEffect(() => {
    if (shouldScrollForRedeem.current) {
      // This is causing the double scroll - remove this entire useEffect
      ...
    }
  }, [redeemDataMapping, autoScrollDisabled]);
  */

  // Also remove or comment out the offer detail scroll useEffect if it's causing issues
  /*
  useEffect(() => {
    if (shouldScrollForOfferDetail.current) {
      // This might also cause double scroll - remove if needed
      ...
    }
  }, [offerDetailMapping, autoScrollDisabled]);
  */

  // Remove the duplicate useEffect and keep only one main scroll effect
  useEffect(() => {
    const prevActivities = prevActivitiesRef.current;
    const newActivities = activities.filter(
      (activity) => !prevActivities.find((a) => a.id === activity.id)
    );

    const hasNewMessage = newActivities.some(
      (activity) => activity.type === "message" || activity.type === "typing"
    );

    if (hasNewMessage && !autoScrollDisabled) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 10);
      });
    }

    prevActivitiesRef.current = activities;

    const lastMessage = activities
      .filter((activity) => activity.type === "message")
      .pop();

    if (lastMessage) {
      const messageText = lastMessage.text;
      const arabicRegex = /[\u0600-\u06FF]/;
      const isArabicMessage = arabicRegex.test(messageText);
      const isNumeric = /^-?\d+(\.\d+)?$/.test(messageText);

      if (!messageText || messageText === "") {
        return;
      } else if (isNumeric) {
        return;
      } else if (isArabicMessage && userLastMessageArabic !== true) {
        setUserLastMessageArabic(true);
      } else if (!isArabicMessage && userLastMessageArabic !== false) {
        setUserLastMessageArabic(false);
      }
    }
  }, [activities, autoScrollDisabled, userLastMessageArabic]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (offerDetailScrollTimeout.current) {
        clearTimeout(offerDetailScrollTimeout.current);
      }
      if (redeemScrollTimeout.current) {
        clearTimeout(redeemScrollTimeout.current);
      }
      if (viewDetailsScrollTimeout.current) {
        clearTimeout(viewDetailsScrollTimeout.current);
      }
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
      if (disableTimeoutRef.current) {
        clearTimeout(disableTimeoutRef.current);
      }
    };
  }, []);

//STYLING CONSTANTS

const MOBILE_WIDTH = 768;
const COLOR_DISABLED = "#94c3ba";
const COLOR_ENABLED = "#007560";
const BG_COLOR_INPUT = "#f6f6f6";
const BG_COLOR_BUTTON_DISABLED = "#d3d3d3";
const OUTLINE_COLOR_RECORDING = "1px solid #007560";
const DROP_SHADOW_ACTIVE = "drop-shadow(0 0 10px rgba(0, 117, 96, 0.8))";
const DROP_SHADOW_INACTIVE = "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))";
const isMobile = window.innerWidth <= MOBILE_WIDTH;
const inputHeight = isMobile ? "35px" : "40px";
const inputPadding = isMobile ? "6px" : "8px";
const fontSize = isMobile ? "0.9rem" : "1.125rem"; // Reduced from 0.9rem and 1.125rem
const flexDirection = isArabic ? "row-reverse" : "row";
const direction = isArabic ? "rtl" : "ltr";
const placeholderText = isArabic ? "كيف يمكنني مساعدتك؟" : "How can I help you?";

// Common styles for input and buttons USED FOR DYNAMIC RENDERING
const inputStyle = {
  backgroundColor: BG_COLOR_INPUT,
  flex: 1,
  height: inputHeight,
  padding: inputPadding,
  borderRadius: "12px",
  outline: "none",
  textAlign: isArabic ? "right" : "left",
  width: "100%",
  boxSizing: "border-box",
};

const submitButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  color: "white",
  cursor: !sendBoxValue || streamingText || isThinking ? "not-allowed" : "pointer",
  backgroundColor: !sendBoxValue || streamingText || isThinking ? COLOR_DISABLED : COLOR_ENABLED,
  borderRadius: "30px",
};

const buttonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: isMobile ? "55px" : "50px",
  height: isMobile ? "55px" : "50px",
  margin: isMobile ? "8px" : "10px",
  marginLeft: userLastMessageArabic ? "0.5rem" : "",
  marginRight: userLastMessageArabic ? "" : "0.5rem",
  borderRadius: "16px",
  backgroundColor: "white", // Always white background
  cursor: isSurveyButtonDisabled ? "not-allowed" : "pointer",
};

// Style for outer padding based on screen size
const outerPaddingStyle = isMobile
  ? { padding: "0" }
  : { paddingLeft: "1rem", paddingRight: "1rem" }; // Reduced padding

// Update the scrollbar styles
const scrollbarBaseStyle = {
  '::-webkit-scrollbar': {
    width: 'thin',
    background: 'transparent'
  },
  '::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '10px'
  },
  '::-webkit-scrollbar-thumb': {
    borderRadius: '10px',
    border: '1px solid transparent',
    backgroundClip: 'content-box',
    transition: 'background-color 0.3s ease'
  },
  // For Firefox
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent',
  transition: 'scrollbar-color 0.3s ease'
};

const scrollbarVisibleStyle = {
  ...scrollbarBaseStyle,
  '::-webkit-scrollbar-thumb': {
    ...scrollbarBaseStyle['::-webkit-scrollbar-thumb'],
    backgroundColor: 'rgba(128, 128, 128, 0.5)'
  },
  // For Firefox
  scrollbarColor: 'rgba(128, 128, 128, 0.5) transparent'
};

const scrollbarHiddenStyle = {
  ...scrollbarBaseStyle,
  '::-webkit-scrollbar-thumb': {
    ...scrollbarBaseStyle['::-webkit-scrollbar-thumb'],
    backgroundColor: 'transparent'
  },
  // For Firefox
  scrollbarColor: 'transparent transparent'
};

// Render the UI
return (
  <div className="bg-white" style={outerPaddingStyle}>
    <div className="w-full bg-white flex flex-col" style={{ height: '100vh' }}>
      {/* Scrollable messages area */}
      <div 
        className="flex-1 overflow-y-auto"
        style={{ 
          backgroundColor: 'transparent',
          ...(isScrolling ? scrollbarVisibleStyle : scrollbarHiddenStyle)
        }}
        onScroll={handleUserScroll}
        onMouseEnter={() => setIsScrolling(true)}
        onMouseLeave={() => {
          if (scrollTimerRef.current) {
            clearTimeout(scrollTimerRef.current);
          }
          scrollTimerRef.current = setTimeout(() => {
            setIsScrolling(false);
          }, 1000);
        }}
      >
        <div className="flex flex-col p-4 max-w-5xl mx-auto" style={{ fontSize }}>
          <div className="flex-1 p-2 md:p-4">
            <ul className="space-y-4 relative">
              {activities.filter(({ type }) => type === "message").length <= 1 && (
                <WelcomeTiles setIsThinking={setIsThinking} isArabic={isArabic} loaded={activities.length === 0} basicConfig={basicConfig} />
              )}

              {activities.filter(({ type }) => type === "message").map((activity, index) => {
                const basicLogoUrl = basicConfig?.basicLogoUrl;
                const feedback = responseFeedbackMapping[activity.id] || 0;
                const suggestionsandsources = activity.suggestedActions?.actions || [];
                const sources = suggestionsandsources.filter(item => item.type === "openUrl");
                const suggestedActions = suggestionsandsources.filter(item => item.type !== "openUrl");
                const surveyData = surveyMapping[activity.id];
                const language = arabicMessages[activity.id];
                const offerData = offerDataMapping[activity.id];
                const pendingActionCards = pendingActionCardsMapping[activity.id];
                const paymentReceipt = paymentReceiptMapping[activity.id];
                const offerDetail = offerDetailMapping[activity.id];
                const redeemData = redeemDataMapping[activity.id];
                const viewDetailsData = viewDetailsMapping[activity.id]; // Add view details data

                const commonMessageItemProps = {
                  isArabic: language,
                  activity,
                  id: activity.id,
                  responseGood: feedback,
                  surveyData,
                  suggestedActionlist: suggestedActions,
                  offerData,
                  pendingActionCards,
                  paymentReceipt,
                  offerDetail: offerDetail,
                  redeemData: redeemData,
                  viewDetailsData: viewDetailsData, // Pass view details data
                  disableSend: (!!streamingText || isThinking || activities.length === 0),
                  triggeredby: activity.channelData?.triggeredby,
                  basicLogoUrl: basicLogoUrl
                };

                return index === 0 ? (
                  <React.Fragment key={`fragment-${activity.id}`}>
                    <DateDivider date={activity.timestamp} isArabic={isArabic} />
                    <MessageItem {...commonMessageItemProps} isLastMessage={true} />
                  </React.Fragment>
                ) : (
                  <MessageItem
                    {...commonMessageItemProps}
                    locked={!!streamingText || isThinking}
                    key={activity.id || `message-${index}`}
                    sources={sources}
                    onSuggestionClick={onSuggestionClick}
                  />
                );
              })}

              {isThinking && (
                <MessageItem
                  isArabic={userLastMessageArabic}
                  key="thinking"
                  activity={{
                    id: "thinking",
                    text: userLastMessageArabic ? "البوت يفكر..." : "Bot is thinking...",
                    type: "typing",
                    from: { role: "agent" },
                    channelData: { streamType: "streaming" },
                    timestamp: new Date().toISOString(),
                  }}
                />
              )}

              {streamingText && (
                <MessageItem
                  isArabic={userLastMessageArabic}
                  key="streaming"
                  activity={{
                    id: "streaming",
                    text: streamingText,
                    type: "typing",
                    from: { role: "agent" },
                    timestamp: new Date().toISOString(),
                    channelData: { streamType: "streaming" },
                  }}
                />
              )}

              <div ref={messagesEndRef} />
            </ul>
          </div>
        </div>
      </div>

      {/* Fixed bottom section for input and suggestions */}
      <div className="flex-shrink-0 bg-white" style={{ maxWidth: '100%' }}>
        <div className="max-w-5xl mx-auto px-4">
          {suggestions.length > 0 && (
            <SuggestionList suggestions={suggestions} onSuggestionClick={onSuggestionClick} />
          )}

          <div className="mt-4" style={{ marginBottom: isMobile ? "0" : "1rem" }}>
            <form onSubmit={handleSubmit} className="flex items-center" style={{ flex: "1" }}>
              {!isArabic && (
                <button
                  onClick={onDemandSurvey}
                  type="button"
                  className={`bg-gray-200 rounded-full ${isMobile ? '' : 'ml-2'}`}
                  style={buttonStyle}
                >
                  <RiUserSmileLine 
                    className="text-6xl" 
                    style={{ 
                      color: COLOR_ENABLED,
                      opacity: isSurveyButtonDisabled ? 0.2 : 1,
                      transition: "opacity 0.2s ease-in-out"
                    }} 
                  />
                </button>
              )}

              {isArabic && (
                <button
                  onClick={onDemandSurvey}
                  onKeyDown={handleArabicSurveyKeyDown}
                  type="button"
                  className={`bg-gray-200 rounded-full ${isMobile ? '' : 'ml-2'}`}
                  style={{
                    ...buttonStyle,
                    order: 2,
                  }}
                >
                  <RiUserSmileLine 
                    className="text-6xl"
                    style={{ 
                      color: isSurveyButtonDisabled ? COLOR_DISABLED : COLOR_ENABLED,
                      opacity: isSurveyButtonDisabled ? 0.2 : 1,
                      transition: "opacity 0.2s ease-in-out"
                    }} 
                  />
                </button>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: BG_COLOR_INPUT,
                  border: "1px solid #D1D5DB",
                  borderRadius: "16px",
                  padding: "8px",
                  flex: "1",
                  marginBottom: "4px",
                  outline: isRecording ? OUTLINE_COLOR_RECORDING : "none",
                  filter: isRecording ? DROP_SHADOW_ACTIVE : DROP_SHADOW_INACTIVE,
                  transition: "filter 0.2s ease-in-out, outline 0.2s ease-in-out",
                  flexDirection,
                }}
              >
                <input
                  id="chat-composer-input"
                  autoFocus
                  onChange={handleChange}
                  type="text"
                  value={sendBoxValue}
                  style={inputStyle}
                  dir={direction}
                  placeholder={placeholderText}
                />
                <div style={{ 
                  display: "flex", 
                  alignItems: "center",
                  flexDirection: "row"
                }}>
                  <SpeechToText
                    text={sendBoxValue}
                    setText={setSendBoxValue}
                    recording={isRecording}
                    setRecording={setIsRecording}
                  />
                  {showHandsFreeButton && (
                    <button
                      onClick={() => setIsHandsFreeActive(true)}
                      type="button"
                      disabled={!!streamingText || isThinking}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        display: "flex",
                        alignItems: "center",
                        padding: "6px",
                        marginLeft: isArabic ? "4px" : "0",
                        marginRight: isArabic ? "0" : "4px",
                        cursor: (!!streamingText || isThinking) ? "not-allowed" : "pointer"
                      }}
                      title={isArabic ? "تفعيل الوضع الحر" : "Enable Hands-Free Mode"}
                    >
                      <PiWaveform
                        style={{
                          color: (!!streamingText || isThinking) ? '#94c3ba' : '#007560',
                          fontSize: '1.6rem',
                          opacity: (!!streamingText || isThinking) ? 0.4 : 1,
                          transition: "all 0.2s ease-in-out"
                        }}
                      />
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className="composer-send-btn"
                  disabled={!sendBoxValue || !!streamingText || isThinking || activities.length === 0}
                  style={submitButtonStyle}
                >
                  <FaPaperPlane style={{ fontSize: "1.125rem" }} />
                </button>
              </div>
            </form>
          </div>

          <div
            className={`flex items-center ${!isMobile ? 'mx-auto' : ''}`}
            style={{
              color: "gray",
              direction,
              textAlign: "center",
              justifyContent: "center",
              alignItems: "center",
              paddingBottom: isMobile ? "" : "12px",
              fontSize: isMobile ? "13px" : "15px",
            }}
          >
            {isMobile
              ? isArabic
                ? "رماس مدعوم بتقنية الذكاء الاصطناعي التوليدي، يرجى التحقق من الإجابات"
                : "Rammas uses GenAI; verify answers as needed."
              : 
              basicConfig?.footer ?
                basicConfig.footer.split('<br/>').map((line, index, array) => (
                            <React.Fragment key={index}>
                            {line}
                            {index < array.length - 1 && <br />}
                            </React.Fragment>
                          )) 
              : isArabic
                ? (
                <>
                  رماس، المدعوم بالذكاء الاصطناعي التوليدي، يعتمد على البيانات المتوفرة ويجب التحقق منها عند الضرورة.<br />
                  يرجى تقييم الإجابات للمساعدة في تحسين رماس.
                </>
              )
              : (
                <>
                  Rammas, powered by Generative AI, is based on available data and should be verified further when necessary.<br />
                  Please rate the responses to help improve Rammas.
                </>
          )}
          </div>
        </div>
      </div>
      
      {/* Full-screen HandsFreeMode */}
      {isHandsFreeActive && (
        <HandsFreeMode
          isArabic={userLastMessageArabic}
          onClose={() => setIsHandsFreeActive(false)}
          sendMessage={sendMessage}
        />
      )}
    </div>
  </div>
);

};

export default ChatContent;
